// Per-persona mint multiplication controls (PR-5d, 2026-09-27).
//
// Even with DIRECTOR_PERSONA_MULTIPLICATION on globally, the mint path
// does nothing unless THIS campaign has multiplication enabled. Bounded
// server-side at PERSONA_MULT_CEILING (20); UI capped at 20 too.
//
// Money-visibility contract: the estimator card MUST show honest ad count +
// USD BEFORE the operator saves. Reads the same expander the mint runs
// (via GET /:id/persona-multiplication-estimate) so what you see is what
// you get.

import {
  Card, CardBody, VStack, HStack, Text, Heading, Button, Box, Badge,
  Switch, Spinner, useToast, NumberInput, NumberInputField, Divider,
  Stat, StatLabel, StatNumber, StatHelpText, Alert, AlertIcon
} from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { apiJson } from '../../auth/apiFetch';

type Config = {
  enabled: boolean;
  cap: number | null;
  coverageOnly: boolean;
};

type Estimate = {
  campaignId: string;
  conceptsPerProduct: number;
  staticFormats: number;
  productCount: number;
  baselineAds: number;
  multipliedAds: number;
  estBaselineUsd: number;
  estMultipliedUsd: number;
  unitPriceUsd: number;
  perProduct: Array<{
    productId: string;
    eligiblePersonas: Array<{ personaId: string; name: string }>;
    skippedPersonas: Array<{ personaId: string; name: string; reason: string }>;
    variants: number;
    ads: number;
  }>;
};

export function PersonaMultiplicationCard({
  campaignId,
  productIds,
  initialConfig,
  onChanged
}: {
  campaignId: string;
  productIds: string[]; // from campaign.productSetIds || matchedProductIds
  initialConfig: Partial<Config> | null | undefined;
  onChanged: () => void;
}) {
  const [config, setConfig] = useState<Config>(() => ({
    enabled:      initialConfig?.enabled === true,
    cap:          initialConfig?.cap ?? null,
    coverageOnly: initialConfig?.coverageOnly !== false
  }));
  const [initial, setInitial] = useState<Config>(() => ({
    enabled:      initialConfig?.enabled === true,
    cap:          initialConfig?.cap ?? null,
    coverageOnly: initialConfig?.coverageOnly !== false
  }));
  const [saving, setSaving] = useState(false);
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [estimateErr, setEstimateErr] = useState<string | null>(null);
  const toast = useToast();

  const loadEstimate = useCallback(async () => {
    if (!productIds.length) { setEstimate(null); return; }
    setEstimating(true);
    setEstimateErr(null);
    try {
      // Pass a representative static-format count of 3 (Meta trio); the
      // real mint uses the wizard's picked formats. The card shows an
      // indicative Meta-shape estimate; per-product/format detail lives
      // in the wizard on Generate.
      const params = new URLSearchParams({
        productIds:    productIds.slice(0, 20).join(','), // cap URL size
        staticFormats: '3'
      });
      const r = await apiJson<Estimate>(
        `/api/campaigns/${encodeURIComponent(campaignId)}/persona-multiplication-estimate?${params}`
      );
      setEstimate(r);
    } catch (err) {
      setEstimateErr(err instanceof Error ? err.message : 'Failed to load estimate');
    } finally {
      setEstimating(false);
    }
  }, [campaignId, productIds]);

  useEffect(() => { void loadEstimate(); }, [loadEstimate]);

  const dirty =
    config.enabled !== initial.enabled ||
    (config.cap ?? null) !== (initial.cap ?? null) ||
    config.coverageOnly !== initial.coverageOnly;

  const save = async () => {
    if (saving || !dirty) return;
    setSaving(true);
    try {
      await apiJson<{ personaMultiplication: Config }>(
        `/api/campaigns/${encodeURIComponent(campaignId)}/persona-multiplication`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(config)
        }
      );
      setInitial({ ...config });
      toast({
        title: config.enabled
          ? 'Persona multiplication enabled'
          : 'Persona multiplication disabled',
        description: config.enabled
          ? `Each concept will fan into up to ${config.cap ?? 5} persona variants on the next Generate.`
          : 'Mint will produce one ad per concept (standard behavior).',
        status: 'success',
        duration: 3500
      });
      onChanged();
      // Refresh the estimate — the operator will want to see the new
      // multiplier take effect on the count before hitting Generate.
      void loadEstimate();
    } catch (err) {
      toast({
        title: 'Save failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        status: 'error',
        duration: 5000
      });
    } finally {
      setSaving(false);
    }
  };

  const revert = () => setConfig({ ...initial });

  // Aggregate skipped personas across products for the warning line.
  const skippedUniq = new Set<string>();
  const skippedByReason: Record<string, number> = {};
  if (estimate) {
    for (const p of estimate.perProduct) {
      for (const s of p.skippedPersonas) {
        skippedUniq.add(s.personaId);
        skippedByReason[s.reason] = (skippedByReason[s.reason] || 0) + 1;
      }
    }
  }

  return (
    <Card variant="outline">
      <CardBody>
        <HStack justify="space-between" mb={3} align="flex-start">
          <VStack align="flex-start" spacing={0}>
            <Heading size="sm" textTransform="uppercase" letterSpacing="0.04em">
              Persona Multiplication
            </Heading>
            <Text fontSize="xs" color="brand.muted" mt={1}>
              Fan each Director concept into per-persona variants at mint. Each variant becomes its own billable ad.
            </Text>
          </VStack>
          {dirty && (
            <HStack spacing={2}>
              <Button size="xs" variant="ghost" onClick={revert} isDisabled={saving}>Revert</Button>
              <Button size="xs" colorScheme="purple" onClick={save} isLoading={saving} loadingText="Saving…">
                Save
              </Button>
            </HStack>
          )}
        </HStack>

        <VStack align="stretch" spacing={4}>
          {/* Enable toggle */}
          <HStack justify="space-between">
            <VStack align="flex-start" spacing={0}>
              <Text fontSize="sm" fontWeight="medium">Enable multiplication</Text>
              <Text fontSize="11px" color="brand.muted">
                Off by default. Requires DIRECTOR_PERSONA_MULTIPLICATION flag to be enabled server-side.
              </Text>
            </VStack>
            <Switch
              isChecked={config.enabled}
              onChange={e => setConfig({ ...config, enabled: e.target.checked })}
              colorScheme="purple"
            />
          </HStack>

          {/* Cap */}
          <HStack justify="space-between" opacity={config.enabled ? 1 : 0.5}>
            <VStack align="flex-start" spacing={0}>
              <Text fontSize="sm" fontWeight="medium">Max personas per concept</Text>
              <Text fontSize="11px" color="brand.muted">
                Server-clamped to 20. Empty = service default (5).
              </Text>
            </VStack>
            <NumberInput
              size="sm"
              maxW="90px"
              min={1}
              max={20}
              value={config.cap ?? ''}
              onChange={(_, valueAsNumber) => {
                setConfig({ ...config, cap: Number.isFinite(valueAsNumber) ? valueAsNumber : null });
              }}
              isDisabled={!config.enabled}
            >
              <NumberInputField placeholder="5" />
            </NumberInput>
          </HStack>

          {/* Coverage-only */}
          <HStack justify="space-between" opacity={config.enabled ? 1 : 0.5}>
            <VStack align="flex-start" spacing={0}>
              <Text fontSize="sm" fontWeight="medium">Coverage-only</Text>
              <Text fontSize="11px" color="brand.muted">
                Skip personas without a lifestyle image for the product. Safer — avoids persona-labeled duplicate renders.
              </Text>
            </VStack>
            <Switch
              isChecked={config.coverageOnly}
              onChange={e => setConfig({ ...config, coverageOnly: e.target.checked })}
              isDisabled={!config.enabled}
              colorScheme="purple"
            />
          </HStack>

          <Divider />

          {/* Estimate */}
          <Box>
            <HStack justify="space-between" align="flex-start" mb={2}>
              <Text fontSize="xs" color="brand.muted" textTransform="uppercase" letterSpacing="0.04em">
                Estimated impact (Meta static trio)
              </Text>
              {estimating && <Spinner size="xs" />}
            </HStack>
            {estimateErr ? (
              <Alert status="error" fontSize="xs">
                <AlertIcon />
                {estimateErr}
              </Alert>
            ) : !estimate ? (
              <Text fontSize="xs" color="brand.muted" fontStyle="italic">
                {productIds.length ? 'Loading estimate…' : 'No products on this campaign yet.'}
              </Text>
            ) : (
              <VStack align="stretch" spacing={3}>
                <HStack spacing={6}>
                  <Stat size="sm">
                    <StatLabel fontSize="10px" color="brand.muted">Baseline</StatLabel>
                    <StatNumber fontSize="lg">{estimate.baselineAds} ads</StatNumber>
                    <StatHelpText fontSize="11px" mb={0}>~${estimate.estBaselineUsd.toFixed(2)}</StatHelpText>
                  </Stat>
                  <Box alignSelf="center" fontSize="xl" color="brand.muted">→</Box>
                  <Stat size="sm">
                    <StatLabel fontSize="10px" color="brand.muted">With multiplication</StatLabel>
                    <StatNumber fontSize="lg" color={estimate.multipliedAds > estimate.baselineAds ? 'purple.600' : undefined}>
                      {estimate.multipliedAds} ads
                    </StatNumber>
                    <StatHelpText fontSize="11px" mb={0}>~${estimate.estMultipliedUsd.toFixed(2)}</StatHelpText>
                  </Stat>
                  {estimate.multipliedAds > 0 && estimate.baselineAds > 0 && (
                    <Badge colorScheme={estimate.multipliedAds > estimate.baselineAds * 2 ? 'red' : 'purple'} alignSelf="center">
                      {(estimate.multipliedAds / estimate.baselineAds).toFixed(1)}× spend
                    </Badge>
                  )}
                </HStack>
                {skippedUniq.size > 0 && (
                  <Alert status="warning" fontSize="xs">
                    <AlertIcon />
                    <Text>
                      {skippedUniq.size} persona{skippedUniq.size === 1 ? '' : 's'} skipped across products
                      {skippedByReason.no_lifestyle_coverage
                        ? ' — lifestyle image missing (coverage-only mode)'
                        : skippedByReason.cap_exceeded
                        ? ' — cap exceeded'
                        : ''}
                    </Text>
                  </Alert>
                )}
                <Text fontSize="10px" color="brand.muted">
                  Concepts per product: {estimate.conceptsPerProduct} · Products: {estimate.productCount}
                  · Est. unit ${estimate.unitPriceUsd.toFixed(2)}/ad (Meta static baseline)
                </Text>
              </VStack>
            )}
          </Box>
        </VStack>
      </CardBody>
    </Card>
  );
}
